// src/lib/intake/section23Facts.js
//
// Ticket 064 (Advisor Intake I5). Pure mapping for Sections 2 and 3, like
// section1Facts.js: what the person answered on the screens <-> the `domains`
// body of intake-save. No React, no network.
//
// ANSWERS SLICES (added to the Section 1 answers by emptyAnswers23):
//   events         null | { <the 13 event values> } booleans.
//                  null = unanswered; ALL false (and ownership null) = "None of these".
//   incomeItems    card-shaped items for BOTH owners (what IncomeCard edits)
//   workLocation   { [employment item id]: work location value } (D8)
//   none           the "I had no income this year" button (self); `spouse` is
//                  kept for symmetry but the spouse page has no such button
//   spouseHasIncome null | true | false (the spouse gate, married only)
//   autonomo       plain values for AutonomoFactsForm (nulls until answered)
//   beckham        null | 'active' | 'not_active'
//   ownership      null | { spain, outside, countries: [ISO codes] }: "Owned property"
//                  is ticked with these follow-up answers (amendment 2). null with
//                  events answered = no property owned.
//   skipIncome     true = "Skip income for now" (stored as self_income_items unknown)
//   saleLocation   null | 'spain' | 'us' | 'other' (never 'unknown', D4)
//   assets         { netWorth, foreignAccounts }: band values or null (screen 4.1,
//                  ticket 066). Required; never 'unknown', and 'none' is a real value.
//
// WHAT EACH SCREEN WRITES (one intake-save per Next):
//   2.1 events + property  events (13 facts) and the property ownership facts
//   2.2 2.3 2.3s 2.4      income, replaced WHOLE every time (both owners'
//                         items, the autonomo block when there is one, both counts)
//   2.5                   household.beckham_status
//   3.3                   property.sale_location
//   3.4                   nothing (advice only)
//   4.1                   assets (both bands, known_value)
// Suggestions and nudges are advice and are never stored.

import { buildSection1Domains, answersFromFacts, firstOpenScreen as firstOpenSection1, emptyAnswers, known, none } from './section1Facts'
import {
  SECTION23_SCREEN_ORDER,
  ALL_GROUP_KEYS,
  MAX_ITEMS,
  MAJOR_EVENT_OPTIONS,
} from './intakeConfig'
import {
  cardConfigFor,
  isTouched,
  cardItemsToIncomeFacts,
  incomeFactsToCardItems,
  autonomoCardToFacts,
  autonomoFactsToCard,
  emptyAutonomoCard,
  isAutonomoComplete,
} from './incomeCardMapper'
import { INCOME_CARD_CONFIG } from '../../hooks/incomeCardConfig'

const EVENT_KEYS = MAJOR_EVENT_OPTIONS.map((o) => o.value)

export function emptyAnswers23() {
  return {
    events: null,
    incomeItems: [],
    workLocation: {},
    none: { self: false, spouse: false },
    spouseHasIncome: null,
    autonomo: emptyAutonomoCard(),
    beckham: null,
    ownership: null,
    skipIncome: false,
    saleLocation: null,
    assets: { netWorth: null, foreignAccounts: null },
  }
}

// Answers always carry every slice (a tab-only draft from an older build may not,
// including the Section 1 filing slice added by ticket 066).
export function withAnswers23(answers) {
  return { ...emptyAnswers(), ...emptyAnswers23(), ...(answers || {}) }
}

// ── Who, which cards ─────────────────────────────────────────────────────────

export function isMarried(answers) {
  const m = answers.facts && answers.facts.marital_status
  return !!m && m.status === 'known_value' && m.value === 'married'
}

export function categoryOf(item) {
  const cfg = cardConfigFor(item.income_type)
  return cfg ? cfg.category : null
}

export function cardTypesFor(groupKeys) {
  return INCOME_CARD_CONFIG.filter((c) => groupKeys.includes(c.category))
}

function ownerOf(item) {
  return item.owner === 'spouse' ? 'spouse' : 'self'
}

// All items of one owner in the given category groups (touched or not).
export function itemsIn(answers, owner, groupKeys) {
  return answers.incomeItems.filter((i) => ownerOf(i) === owner && groupKeys.includes(categoryOf(i)))
}

export function touchedIn(answers, owner, groupKeys) {
  return itemsIn(answers, owner, groupKeys).filter(isTouched)
}

export function untouchedCount(answers, owner, groupKeys) {
  return itemsIn(answers, owner, groupKeys).length - touchedIn(answers, owner, groupKeys).length
}

// The card screens: who they are for and which groups they show (all four).
export const CARD_SCREENS = {
  '2.2': { owner: 'self', groups: ALL_GROUP_KEYS, noneKey: 'self' },
  '2.3s': { owner: 'spouse', groups: ALL_GROUP_KEYS, noneKey: 'spouse' },
}

export function isCardScreen(id) {
  return Object.prototype.hasOwnProperty.call(CARD_SCREENS, id)
}

// ── Cap (D5) ─────────────────────────────────────────────────────────────────

export function totalItems(answers) {
  return answers.incomeItems.length
}

// Would replacing the items of one (card type, owner) with `next` take the total
// above the cap? Only an increase can be refused.
export function wouldExceedCap(answers, cardType, owner, next) {
  const current = answers.incomeItems.filter((i) => i.income_type === cardType && ownerOf(i) === owner).length
  if (next.length <= current) return false
  return totalItems(answers) - current + next.length > MAX_ITEMS
}

// Replace the items of (cardType, owner). Returns the new answers, or null when
// the change would go over the cap (the caller shows the notice and keeps state).
export function replaceCardItems(answers, cardType, owner, next) {
  if (wouldExceedCap(answers, cardType, owner, next)) return null
  const others = answers.incomeItems.filter((i) => !(i.income_type === cardType && ownerOf(i) === owner))
  const incomeItems = [...others, ...next]
  const ids = new Set(incomeItems.map((i) => i.id))
  const workLocation = {}
  for (const [id, v] of Object.entries(answers.workLocation)) if (ids.has(id)) workLocation[id] = v
  // Adding a card means the person does have income of that kind.
  const noneNext = { ...answers.none }
  let skip = answers.skipIncome
  if (next.length > 0) {
    noneNext[owner] = false
    if (owner === 'self') skip = false
  }
  return { ...answers, incomeItems, workLocation, none: noneNext, skipIncome: skip }
}

export function removeUntouched(answers, screenId) {
  const cfg = CARD_SCREENS[screenId]
  if (!cfg) return { answers, removed: 0 }
  const drop = new Set(itemsIn(answers, cfg.owner, cfg.groups).filter((i) => !isTouched(i)).map((i) => i.id))
  if (drop.size === 0) return { answers, removed: 0 }
  const workLocation = { ...answers.workLocation }
  for (const id of drop) delete workLocation[id]
  return {
    answers: { ...answers, incomeItems: answers.incomeItems.filter((i) => !drop.has(i.id)), workLocation },
    removed: drop.size,
  }
}

// Removes every card on a cards screen (used by the "No ... income" buttons).
export function removeScreenCards(answers, screenId) {
  const cfg = CARD_SCREENS[screenId]
  if (!cfg) return answers
  const drop = new Set(itemsIn(answers, cfg.owner, cfg.groups).map((i) => i.id))
  if (drop.size === 0) return answers
  const workLocation = { ...answers.workLocation }
  for (const id of drop) delete workLocation[id]
  return { ...answers, incomeItems: answers.incomeItems.filter((i) => !drop.has(i.id)), workLocation }
}

// ── Sale location is fixed by a property-sale card (D4) ──────────────────────

const SALE_SUBTYPES = ['property_sale_primary', 'property_sale_investment']
const LOCATION_OF_COUNTRY = (c) => (c === 'ES' ? 'spain' : c === 'US' ? 'us' : 'other')

// The country a touched property-sale capital-gain card fixes, or null when no
// such card exists or the cards do not agree.
export function saleLocationFromCards(answers) {
  const locations = new Set()
  let cards = 0
  for (const i of answers.incomeItems) {
    if (i.income_type !== 'capital_gain' || !SALE_SUBTYPES.includes(i.income_subtype) || !isTouched(i)) continue
    cards += 1
    if (i.source_country) locations.add(LOCATION_OF_COUNTRY(i.source_country))
    else return null
  }
  return cards > 0 && locations.size === 1 ? [...locations][0] : null
}

function soldProperty(answers) {
  return !!answers.events && answers.events.sold_property === true
}

// The deterministic old three-way vocabulary from the stated countries. The SERVER
// does the same in propertyFacts.derivedOwnership (the single source of truth);
// this copy is for display and for the facts sent, and a test pins both tables.
export function derivedOwnership(countries) {
  return { us: countries.includes('US'), other: countries.some((c) => c !== 'US') }
}

// Is the Owned property follow-up answered enough to move on?
export function ownershipComplete(ownership) {
  if (ownership === null) return true
  if (!ownership.spain && !ownership.outside) return false
  return !ownership.outside || ownership.countries.length > 0
}

// ── Nudges (advice from the server, never stored) ────────────────────────────

export function nudgeKey(n) {
  return `${n.event}:${n.income_type}`
}

export function nudgesFrom(suggestions, dismissed = []) {
  const list = suggestions && suggestions.comparison && Array.isArray(suggestions.comparison.said_but_missing)
    ? suggestions.comparison.said_but_missing
    : []
  return list.filter((n) => !dismissed.includes(nudgeKey(n)))
}

// Card types the server suggests, in the server's order (work, property,
// investments, retirement). Includes types already entered: the block a card sits
// in must not change while the person edits it. There is no per-card badge; the
// block heading says "Suggested for you" (IntakeIncomeScreen).
export function suggestedCardTypes(suggestions) {
  const cards = suggestions && Array.isArray(suggestions.cards) ? suggestions.cards : []
  return cards.map((c) => c.income_type)
}

// ── Which screens exist for this person ──────────────────────────────────────

// ctx = { answers, suggestions, dismissed }
export function shouldShowScreen(id, ctx) {
  const { answers } = ctx
  switch (id) {
    case '2.3': return isMarried(answers)
    case '2.3s': return isMarried(answers) && answers.spouseHasIncome === true
    case '2.4': return touchedIn(answers, 'self', ALL_GROUP_KEYS).some((i) => i.income_type === 'self_employment')
    case '3.3': return soldProperty(answers) && saleLocationFromCards(answers) === null
    // Hidden while income was skipped (nothing to compare with).
    case '3.4': return !answers.skipIncome && nudgesFrom(ctx.suggestions, ctx.dismissed).length > 0
    default: return SECTION23_SCREEN_ORDER.includes(id)
  }
}

export function visibleScreens(ctx) {
  return SECTION23_SCREEN_ORDER.filter((id) => shouldShowScreen(id, ctx))
}

// ── Completeness: the same check the Next button uses ────────────────────────

const employmentOf = (items) => items.filter((i) => i.income_type === 'employment')

function locationsDone(answers, items) {
  return employmentOf(items).every((i) => !!answers.workLocation[i.id])
}

export function spouseTotal(answers) {
  return touchedIn(answers, 'spouse', ALL_GROUP_KEYS).length
}

export function isScreenComplete23(id, ctx) {
  const { answers } = ctx
  switch (id) {
    case '2.1':
      return answers.events !== null && ownershipComplete(answers.ownership)
    case '2.2':
    case '2.3s': {
      const cfg = CARD_SCREENS[id]
      const touched = touchedIn(answers, cfg.owner, cfg.groups)
      // Skipping is allowed on the filer's page; the spouse page may be empty only
      // when the filer skipped (N3).
      if (id === '2.3s' && touched.length === 0 && !answers.skipIncome) return false
      if (id === '2.2' && !(touched.length > 0 || answers.none.self || answers.skipIncome)) return false
      return locationsDone(answers, touched)
    }
    case '2.3':
      return answers.spouseHasIncome !== null
    case '2.4':
      return isAutonomoComplete(answers.autonomo)
    case '2.5':
      return answers.beckham !== null
    case '3.3':
      return answers.saleLocation !== null
    case '3.4':
      return true
    case '4.1':
      return !!answers.assets && answers.assets.netWorth !== null && answers.assets.foreignAccounts !== null
    default:
      return false
  }
}

// First Section 2/3 screen still to answer, or null. 3.4 is advice, never "open".
export function firstOpenScreen23(ctx) {
  for (const id of visibleScreens(ctx)) {
    if (id === '3.4') continue
    if (!isScreenComplete23(id, ctx)) return id
  }
  return null
}

export function nextScreen23(id, ctx) {
  const list = visibleScreens(ctx)
  const i = list.indexOf(id)
  if (i >= 0) return i + 1 < list.length ? list[i + 1] : null
  // `id` is not shown any more (its answers changed): the next shown screen after it.
  const after = SECTION23_SCREEN_ORDER.slice(SECTION23_SCREEN_ORDER.indexOf(id) + 1)
  return after.find((s) => list.includes(s)) || null
}

export function previousScreen23(id, ctx) {
  const list = visibleScreens(ctx)
  const i = list.indexOf(id)
  if (i > 0) return list[i - 1]
  if (i === 0) return null
  const before = SECTION23_SCREEN_ORDER.slice(0, SECTION23_SCREEN_ORDER.indexOf(id)).reverse()
  return before.find((s) => list.includes(s)) || null
}

// ── answers -> domains ───────────────────────────────────────────────────────

export function buildIncomeDomain(answers) {
  const married = isMarried(answers)
  const spouseOn = married && answers.spouseHasIncome === true
  const mine = answers.incomeItems.filter((i) => ownerOf(i) === 'self')
  const theirs = spouseOn ? answers.incomeItems.filter((i) => ownerOf(i) === 'spouse') : []
  const items = cardItemsToIncomeFacts([...mine, ...theirs], { workLocation: answers.workLocation })

  const selfCount = items.filter((i) => i.owner === 'self').length
  const spouseCount = items.filter((i) => i.owner === 'spouse').length

  const income = { v: 1, items }
  if (selfCount > 0) income.self_income_items = known(selfCount)
  else if (answers.none.self) income.self_income_items = none()
  else if (answers.skipIncome) income.self_income_items = { status: 'unknown' }
  else income.self_income_items = { status: 'unanswered' }

  if (married && answers.spouseHasIncome === false) income.spouse_income_items = none()
  else if (spouseOn && spouseCount > 0) income.spouse_income_items = known(spouseCount)
  else if (spouseOn && answers.skipIncome) income.spouse_income_items = { status: 'unknown' }
  else income.spouse_income_items = { status: 'unanswered' }

  // The autónomo block exists only with an own self-employment card (D9).
  const hasOwnSelfEmployment = items.some((i) => i.owner === 'self' && i.income_type === 'self_employment')
  if (hasOwnSelfEmployment) {
    const a = autonomoCardToFacts(answers.autonomo)
    if (a) income.autonomo = a
  }
  return income
}

// Returns the `domains` body for the screen, or null when nothing is saved.
export function buildSectionDomains(screenId, answers, today = new Date()) {
  switch (screenId) {
    case '2.1': {
      const events = {}
      for (const key of EVENT_KEYS) events[key] = known(answers.events ? answers.events[key] === true : false)
      const o = answers.ownership
      const countries = o && o.outside ? [...o.countries].sort() : []
      const d = derivedOwnership(countries)
      const property = {
        owns_spain_property: known(!!o && o.spain === true),
        owns_us_property: known(d.us),
        owns_other_property: known(d.other),
        foreign_property_countries: countries.length > 0 ? known(countries) : { status: 'not_applicable' },
      }
      return { events, property }
    }
    case '2.2': case '2.3': case '2.3s': case '2.4': case '3.1': case '3.1s':
      return { income: buildIncomeDomain(answers) }
    case '2.5':
      return { household: { beckham_status: known(answers.beckham) } }
    case '3.3':
      return { property: { sale_location: known(answers.saleLocation) } }
    case '3.4':
      return null
    case '4.1':
      return {
        assets: {
          worldwide_net_worth_band: known(answers.assets.netWorth),
          foreign_account_band: known(answers.assets.foreignAccounts),
        },
      }
    default:
      return buildSection1Domains(answers, today)
  }
}

// ── stored facts -> answers ──────────────────────────────────────────────────

const statusIs = (f, ...s) => !!f && s.includes(f.status)

export function answersFromFacts23(allFacts) {
  const out = emptyAnswers23()
  const facts = allFacts || {}
  const events = facts.events || {}
  const household = facts.household || {}
  const property = facts.property || {}
  const income = facts.income || null

  if (EVENT_KEYS.some((k) => statusIs(events[k], 'known_value'))) {
    out.events = {}
    for (const k of EVENT_KEYS) out.events[k] = !!events[k] && events[k].status === 'known_value' && events[k].value === true
  }

  const beckham = household.beckham_status
  if (statusIs(beckham, 'known_value')) out.beckham = beckham.value

  const spainFact = property.owns_spain_property
  const countriesFact = property.foreign_property_countries
  const countries = statusIs(countriesFact, 'known_value') ? [...countriesFact.value] : []
  const legacyForeign = [property.owns_us_property, property.owns_other_property]
    .some((f) => statusIs(f, 'known_value') && f.value === true)
  const spain = statusIs(spainFact, 'known_value') && spainFact.value === true
  // A foreign ownership stored without countries (a Snapshot answer) comes back
  // with no countries chosen, so the person is asked again: we never guess countries.
  if (spain || countries.length > 0 || legacyForeign) {
    out.ownership = { spain, outside: countries.length > 0 || legacyForeign, countries }
  }
  const sale = property.sale_location
  if (statusIs(sale, 'known_value')) out.saleLocation = sale.value

  const assets = facts.assets || {}
  if (statusIs(assets.worldwide_net_worth_band, 'known_value')) out.assets.netWorth = assets.worldwide_net_worth_band.value
  if (statusIs(assets.foreign_account_band, 'known_value')) out.assets.foreignAccounts = assets.foreign_account_band.value

  if (income) {
    const { items, workLocation } = incomeFactsToCardItems(income)
    out.incomeItems = items
    out.workLocation = workLocation
    out.autonomo = autonomoFactsToCard(income.autonomo)

    const self = income.self_income_items
    if (statusIs(self, 'known_zero')) out.none.self = true
    if (statusIs(self, 'unknown')) out.skipIncome = true

    const spouse = income.spouse_income_items
    if (statusIs(spouse, 'known_zero')) out.spouseHasIncome = false
    else if (statusIs(spouse, 'known_value') || statusIs(spouse, 'unknown') || items.some((i) => ownerOf(i) === 'spouse')) out.spouseHasIncome = true
  }
  return out
}

// All answers from stored facts: Section 1 and Sections 2 and 3.
export function allAnswersFromFacts(facts, today = new Date()) {
  return { ...answersFromFacts(facts, today), ...answersFromFacts23(facts) }
}

// Where to resume: the first open Section 1 screen, else the first open Section
// 2 or 3 screen, else null (everything is answered).
export function firstOpenScreenAll(facts, today = new Date(), suggestions = null) {
  const s1 = firstOpenSection1(facts, today)
  if (s1) return s1
  const answers = allAnswersFromFacts(facts, today)
  return firstOpenScreen23({ answers, suggestions, dismissed: [] })
}

export { EVENT_KEYS }
